import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ContentData, getContent } from '../data/content';
import { BRAND } from '../constants/brand';

type Language = 'pl' | 'en';

interface LanguageContextType {
  language: Language;
  content: ContentData;
  isLoading: boolean;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children
}) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  
  // Determine language from URL, fallback to Polish (default)
  const urlLanguage: Language = location.pathname.startsWith('/en') ? 'en' : 'pl';
  const [language, setLanguageState] = useState<Language>(urlLanguage);
  const [content, setContent] = useState<ContentData>(getContent(urlLanguage));

  // Update language when URL changes
  useEffect(() => {
    const newLanguage: Language = location.pathname.startsWith('/en') ? 'en' : 'pl';
    if (newLanguage !== language) {
      setIsLoading(true);
      
      // Simulate loading delay for better UX
      setTimeout(() => {
        setLanguageState(newLanguage);
        setContent(getContent(newLanguage));
        setIsLoading(false);
      }, 300);
    }
  }, [location.pathname, language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    setContent(getContent(lang));
    
    // Update document language
    document.documentElement.lang = lang;
    
    // Store preference in localStorage
    localStorage.setItem('itj-dev-language', lang);
  };

  const toggleLanguage = () => {
    const newLang = language === 'pl' ? 'en' : 'pl';
    setLanguage(newLang);
  };

  // Load saved language preference on mount only
  useEffect(() => {
    const savedLanguage = localStorage.getItem('itj-dev-language') as Language;
    if (savedLanguage && (savedLanguage === 'pl' || savedLanguage === 'en')) {
      // Only set if it's a valid language and different from current URL-based language
      const currentUrlLanguage = location.pathname.startsWith('/en') ? 'en' : 'pl';
      if (savedLanguage !== currentUrlLanguage) {
        setLanguage(savedLanguage);
      }
    }
    // This should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Update meta tags when language changes
  useEffect(() => {
    // Update document title
    document.title = content.meta.title;
    
    // Update document language
    document.documentElement.lang = language;
    
    // Update basic meta tags
    const updateMetaTag = (selector: string, content: string) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    const updateLinkHref = (selector: string, href: string) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('href', href);
      }
    };
    
    // Current URL for dynamic updates
    const currentUrl = `${BRAND.WEBSITE_URL}${location.pathname}`;
    const ogImageUrl = `${BRAND.WEBSITE_URL}/og-image.jpg`;
    const twitterImageUrl = `${BRAND.WEBSITE_URL}/twitter-image.jpg`;
    
    // Basic meta tags
    updateMetaTag('meta[name="title"]', content.meta.title);
    updateMetaTag('meta[name="description"]', content.meta.description);
    updateMetaTag('meta[name="keywords"]', content.meta.keywords);
    updateMetaTag('meta[name="language"]', content.meta.language);
    updateMetaTag('meta[name="author"]', BRAND.COMPANY_NAME);
    
    // Open Graph meta tags
    updateMetaTag('meta[property="og:title"]', content.meta.ogTitle);
    updateMetaTag('meta[property="og:description"]', content.meta.ogDescription);
    updateMetaTag('meta[property="og:locale"]', content.meta.locale);
    updateMetaTag('meta[property="og:url"]', currentUrl);
    updateMetaTag('meta[property="og:image"]', ogImageUrl);
    updateMetaTag('meta[property="og:site_name"]', BRAND.COMPANY_NAME);
    
    // Twitter meta tags
    updateMetaTag('meta[property="twitter:title"]', content.meta.twitterTitle);
    updateMetaTag('meta[property="twitter:description"]', content.meta.twitterDescription);
    updateMetaTag('meta[property="twitter:url"]', currentUrl);
    updateMetaTag('meta[property="twitter:image"]', twitterImageUrl);
    
    // Update canonical URL
    updateLinkHref('link[rel="canonical"]', currentUrl);
    
    // Update hreflang links
    const basePath = location.pathname.replace(/^\/en/, '');
    const polishUrl = `${BRAND.WEBSITE_URL}${basePath}`;
    const englishUrl = `${BRAND.WEBSITE_URL}/en${basePath}`;
    
    updateLinkHref('link[hreflang="pl"]', polishUrl);
    updateLinkHref('link[hreflang="en"]', englishUrl);
    updateLinkHref('link[hreflang="x-default"]', polishUrl);
    
    // Add/Update structured data
    const addStructuredData = () => {
      // Remove existing structured data
      const existingScript = document.querySelector('script[data-structured-data]');
      if (existingScript) {
        existingScript.remove();
      }

      // Create comprehensive structured data
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": BRAND.COMPANY_NAME,
        "alternateName": "ITJ Development",
        "url": BRAND.WEBSITE_URL,
        "logo": `${BRAND.WEBSITE_URL}/logo.png`,
        "image": ogImageUrl,
        "description": content.meta.description,
        "email": BRAND.EMAIL,
        "foundingDate": "2018",
        "founder": {
          "@type": "Person",
          "name": BRAND.FOUNDER_NAME,
          "jobTitle": BRAND.TITLE,
          "email": BRAND.EMAIL
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "PL",
          "addressRegion": "Poland"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "email": BRAND.EMAIL,
          "contactType": "customer service",
          "availableLanguage": ["Polish", "English"]
        },
        "sameAs": [
          BRAND.GITHUB_URL,
          BRAND.LINKEDIN_URL
        ],
        "serviceType": [
          "DevOps Engineering",
          "Cloud Infrastructure",
          "Web Development",
          "Mobile Development",
          "AI Integration",
          "System Architecture",
          "Technical Consulting"
        ],
        "areaServed": {
          "@type": "Country",
          "name": "Poland"
        },
        "knowsAbout": [
          "DevOps",
          "Cloud Computing",
          "Kubernetes",
          "Docker",
          "CI/CD",
          "Infrastructure as Code",
          "Go Programming",
          "PHP Development",
          "System Architecture",
          "Team Leadership"
        ]
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-structured-data', 'true');
      script.textContent = JSON.stringify(structuredData, null, 2);
      document.head.appendChild(script);
    };

    addStructuredData();
    
  }, [content, language, location.pathname]);

  const value: LanguageContextType = {
    language,
    content,
    isLoading,
    setLanguage,
    toggleLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useContent = (): ContentData => {
  const { content } = useLanguage();
  return content;
};
